import type { Signals } from '@morales/shared';
import { formatMoney } from '../format';
import { useLocale } from '../i18n';

/** A number with its direction against the period before: ▲ 12% in green, ▼ 8% in red, a dash when there is no before. */
export function Trend({ pct, goodWhenUp = true, unit = '%' }: { pct: number | null; goodWhenUp?: boolean; unit?: string }) {
  if (pct === null) return <span className="trend muted">—</span>;
  const up = pct > 0;
  const flat = Math.abs(pct) < 1;
  const good = flat ? null : up === goodWhenUp;
  return (
    <span className={flat ? 'trend muted' : good ? 'trend good' : 'trend bad'} aria-label={`${up ? '+' : ''}${pct}${unit}`}>
      {flat ? '=' : up ? '▲' : '▼'} {Math.abs(pct)}{unit}
    </span>
  );
}

/** The signals card: the four numbers that say how the week is going, with direction. Home shows it; the page has the rest. */
export function SignalsHeadline({ s, intlLocale }: { s: Signals; intlLocale: string }) {
  const { t } = useLocale();
  return (
    <dl className="facts plain signals-head">
      <div className="fact">
        <dt>{t('revenueWeek')}</dt>
        <dd className="money">
          {formatMoney(s.revenue.week.now, intlLocale)} <Trend pct={s.revenue.week.pct} />
        </dd>
      </div>
      <div className="fact">
        {/* no year ago: QuickBooks' history before 2026 is not complete (JL, decision 135); the last four weeks' average */}
        <dt>{t('vsFourWeekAvg')}</dt>
        <dd>
          {formatMoney(s.revenue.fourWeeksAvg.before, intlLocale)} <Trend pct={s.revenue.fourWeeksAvg.pct} />
        </dd>
      </div>
      {s.margin ? (
        <div className="fact">
          <dt>{t('marginWeek')}</dt>
          <dd>
            {s.margin.pctNow === null ? '—' : `${s.margin.pctNow}%`} <Trend pct={s.margin.pctNow !== null && s.margin.pctBefore !== null ? Math.round((s.margin.pctNow - s.margin.pctBefore) * 10) / 10 : null} unit=" pts" />
            {s.margin.coveredPct < 100 ? <span className="muted"> · {t('costKnownFor')} {s.margin.coveredPct}%</span> : null}
          </dd>
        </div>
      ) : null}
      <div className="fact">
        <dt>{t('ordersWeek')}</dt>
        <dd>
          {s.orders.week.now} <Trend pct={s.orders.week.pct} /> · {s.orders.activeCustomers.now} {t('customers').toLowerCase()} <Trend pct={s.orders.activeCustomers.pct} />
        </dd>
      </div>
      <div className="fact">
        <dt>{t('paidWeek')}</dt>
        <dd>
          {formatMoney(s.receivables.paid.now, intlLocale)} <Trend pct={s.receivables.paid.pct} />
        </dd>
      </div>
      {s.receivables.dsoDays !== null ? (
        <div className="fact">
          <dt>{t('dso')}</dt>
          <dd>
            {s.receivables.dsoDays} {t('days')} <Trend pct={s.receivables.dsoBefore ? Math.round(((s.receivables.dsoDays - s.receivables.dsoBefore) / s.receivables.dsoBefore) * 1000) / 10 : null} goodWhenUp={false} />
          </dd>
        </div>
      ) : null}
    </dl>
  );
}
